import { Page, Box, Card, HorizontalGrid, Text, EmptyState } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { hydrateFilters } from '../../../services/productService';
import { getDeposits } from '../../../services/depositsService';
import { getSearchParams, handleSearchParams } from '../../../helpers/handleSearchParams';
import { getReportsSellsStock } from '../../../services/reports/sellsService';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import ProductStockTable from '../../UI/Tables/ProductStockTable';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import { parseFilterParam } from '../../../helpers/dates';
import FiltersLikeSells from '../../UI/Reports/FiltersLikeSells';
import { SellsExportModal } from './SellsExportModal';

const ViewReportSells = ({ filters: f, title, subtitle, omitFilters }) => {
  const defaultFilters = {
    genders: {
      name: 'Género',
      slug: 'genders',
      bundle: true,
    },
    categories: {
      name: 'Categorías',
      slug: 'categories',
      bundle: true,
    },
    subcategories: {
      name: 'Subcategorías',
      slug: 'subcategories',
      bundle: true,
    },
    deposits: {
      name: 'Depósitos',
      slug: 'deposits',
    },
    // customers: {
    //   name: 'Clientes',
    //   slug: 'customers',
    //   value: '2',
    // },
    seasons: {
      name: 'Temporada',
      slug: 'seasons',
      bundle: true,
    },
    years: {
      name: 'Año',
      slug: 'years',
      bundle: true,
    },
    ...f,
  };

  const setLabelValue = (inputArray) => inputArray.map((i) => ({ label: i.name, value: i.id }));

  const { timezone } = useTimezone();
  const { currency } = useCurrency();
  const [filters, setFilters] = useState(defaultFilters);
  const [defaultDates, setDefaultDates] = useState(null);
  const [ready, setReady] = useState(false);
  const [stock, setStock] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Filtros activos (value no nulo) en formato querystring para el endpoint
  // del Excel — mismo formato que ya consume `getReportsSellsStock`, más
  // moneda y zona del usuario (las leen `parseCurrency` y `parseRequestTz`
  // del back para formatear el Excel correctamente).
  const exportParams = useMemo(() => {
    const fromFilters = Object.keys(filters)
      .map((k) => (filters[k]?.value ? { slug: k, value: filters[k].value } : null))
      .filter(Boolean)
      .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {});
    return {
      ...fromFilters,
      currency: currency?.symbol,
      exchange: currency?.exchange,
      ...(timezone ? { tz: timezone } : {}),
    };
  }, [filters, currency, timezone]);

  const handleFilter = ({ slug, value }) => setFilters({ ...filters, [slug]: { ...filters[slug], value } });
  const handleDateChange = (dates) => {
    Object.keys(dates).forEach((k) => handleSearchParams(k, dates[k]));

    setFilters({
      ...filters,
      since: {
        value: dates.since,
      },
      until: {
        value: dates.until,
      },
    });
  };

  const clearFilters = () => {
    const currentFilters = { ...filters };
    Object.keys(defaultFilters).forEach((k) => {
      currentFilters[k].value = f?.[k]?.value ?? null;
    });
    setFilters(currentFilters);
  };

  const loadStock = (page = 1, fltr, rebuild = false) => {
    const validFilters = Object.keys(fltr)
      .map((k) => (fltr[k].value ? { slug: k, value: fltr[k].value } : null))
      .filter((f) => f)
      .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {});

    getReportsSellsStock({ page, limit: 50, ...validFilters }).then((r) => {
      r.data.data = r.data.data.map((p) => ({
        ...p,
        pvp_avg:
          p.stock.reduce((acc, cur) => acc + cur.pvp_avg * cur.qty, 0) / p.stock.reduce((acc, cur) => acc + cur.qty, 0),
      }));

      setStock({
        ...r.data,
        new: rebuild,
      });
    });
  };

  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(defaultFilters)
          .map((k) => defaultFilters[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
      // getCustomersList({ limit: 1000 }),
    ]).then(([f, d]) => {
      setFilters({
        ...filters,
        ...f,
        deposits: {
          ...f.deposits,
          options: setLabelValue([...d.data.data]),
        },
        // customers: {
        //   ...f.customers,
        //   options: setLabelValue([...c.data.data]),
        // },
      });

      const period = {
        since: parseFilterParam(getSearchParams('since'), timezone),
        until: parseFilterParam(getSearchParams('until'), timezone),
      };

      if (period.since && period.until) setDefaultDates(period);
      setReady(true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k].value));
    if (!filters.until || !filters.since) return;
    loadStock(1, filters, true);
  }, [filters]);

  return (
    <Page
      title={title || 'Ventas'}
      subtitle={subtitle}
      primaryAction={{
        content: 'Exportar Excel',
        onAction: () => setExportOpen(true),
        disabled: !ready,
      }}
    >
      {ready && (
        <>
          <Card roundedAbove="sm">
            <FiltersLikeSells
              filters={filters}
              handleDateChange={handleDateChange}
              handleFilter={handleFilter}
              defaultDates={defaultDates}
              clearFilters={clearFilters}
              omitFilters={omitFilters}
            />
          </Card>
          <StockWrapper stock={stock} filters={filters} onLoadStock={loadStock} />
        </>
      )}
      <SellsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        exportParams={exportParams}
      />
    </Page>
  );
};

const StockWrapper = ({ stock, filters, onLoadStock }) => {
  return (
    <>
      {stock && (
        <>
          <Box minHeight=".5rem" />
          {!stock.data.length && (
            <>
              <Card roundedAbove="sm">
                <EmptyState
                  heading="No hay datos para mostrar"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                ></EmptyState>
              </Card>
            </>
          )}

          {stock.data.length > 0 && (
            <>
              <Card roundedAbove="sm">
                <Numbers {...stock.metadata} />
                <Box minHeight=".5rem" />
              </Card>

              <ProductsWrapper
                data={stock}
                TableComponent={(args) => <ProductStockTable {...args} />}
                loadPage={() => onLoadStock(stock.metadata.page + 1, filters)}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const Numbers = ({ pvp_avg, pvp, qty }) => {
  const { currency } = useCurrency();

  const numbers = [
    {
      label: 'Cantidad de productos',
      value: Intl.NumberFormat('es-Es').format(qty) + ' Uds.',
    },
    {
      label: 'Venta Real (IVA incluído)',
      value: Intl.NumberFormat('es-Es', {
        style: 'currency',
        currency: currency.symbol,
      }).format(pvp_avg * currency.exchange),
      subvalue:
        Intl.NumberFormat('es-Es', {
          style: 'currency',
          currency: currency.symbol,
        }).format((pvp_avg * currency.exchange) / 1.16) + ' (sin IVA)',
    },
    {
      label: 'Venta Teórica PVP (IVA incluído)',
      value: Intl.NumberFormat('es-Es', {
        style: 'currency',
        currency: currency.symbol,
      }).format(pvp * currency.exchange),
      subvalue:
        Intl.NumberFormat('es-Es', {
          style: 'currency',
          currency: currency.symbol,
        }).format((pvp * currency.exchange) / 1.16) + ' (sin IVA)',
    },
    {
      label: 'Descuento medio',
      value: Intl.NumberFormat('es-Es', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(1 - pvp_avg / pvp),
    },
  ];

  return (
    <HorizontalGrid columns={{ xs: 1, md: numbers.map(() => '1fr').join(' ') }} gap="4">
      {numbers.map((n) => (
        <Box key={n.label}>
          <Text variant="headingSm" as="h4">
            {n.label}
          </Text>
          <Box border="divider" borderRadius="base" minHeight=".5rem" />
          <Text variant="headingXl">{n.value}</Text>
          {n.subvalue && <Text variant="headingSm">{n.subvalue}</Text>}
        </Box>
      ))}
    </HorizontalGrid>
  );
};

export default ViewReportSells;
